import React, { Dispatch, SetStateAction, useState, useRef, useEffect } from "react";
import { GiHamburgerMenu } from "react-icons/gi";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitch from "../Languages/LanguageSwitch";
import { MenuContent } from "./MenuContent";
import ButtonFullWidth from "../Buttons/ButtonFullWidth";
import ReactDOM from "react-dom";

interface HamburgerMenuProps {
  setIsAllMenuOpen: Dispatch<SetStateAction<boolean>>;
  isAllMenuOpen: boolean;
  menuContent?: MenuContent[];
}

function HamburgerMenu({ setIsAllMenuOpen, isAllMenuOpen, menuContent }: Readonly<HamburgerMenuProps>) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Stati per gestire le transizioni
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Gestione apertura/chiusura con transizioni
  useEffect(() => {
    if (isAllMenuOpen) {
      // Apertura: monta il menu e poi mostralo
      setShouldRender(true);
      // Double requestAnimationFrame per assicurarsi che il DOM sia aggiornato
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
      
      // Pulisci timeout di chiusura se esiste
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    } else if (shouldRender) {
      // Chiusura: nascondi il menu e poi smontalo dopo la transizione
      setIsVisible(false);
      closeTimeoutRef.current = setTimeout(() => {
        setShouldRender(false);
      }, 700); // Durata della transizione CSS
    }

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [isAllMenuOpen, shouldRender]);

  // Funzione per chiudere il menu
  const closeMenu = () => {
    setIsAllMenuOpen(false);
  };

  // Funzione per navigare con chiusura animata
  const navigateWithTransition = (route: string) => {
    closeMenu();
    // Aspetta che la transizione finisca prima di navigare
    setTimeout(() => {
      navigate(route);
    }, 700);
  };

  return (
    <>
      {/* Hamburger Button */}
      <button
        type="button"
        onClick={() => setIsAllMenuOpen(!isAllMenuOpen)}
        className="w-full"
        aria-label={isAllMenuOpen ? "Chiudi menu" : "Apri menu"}
        aria-expanded={isAllMenuOpen ? "true" : "false"}
      >
        <GiHamburgerMenu className="text-4xl" />
      </button>

      {/* Overlay Menu */}
      {shouldRender && typeof window !== "undefined" &&
        ReactDOM.createPortal(
          <div
            className={`
              text-font-lightGrey font-semibold
              fixed top-0 right-0 h-screen w-full sm:w-[320px] max-w-full z-[100000]
              bg-lightbg-headerPrimary shadow-2xl border-l border-lightbg-midGrey
              transition-all duration-700 ease-in-out
              transform
              ${isVisible 
                ? "translate-x-0 opacity-100 pointer-events-auto" 
                : "translate-x-full opacity-0 pointer-events-none"}
            `}
          >
            {/* Close button */}
            <button
              className="absolute top-3 right-4 text-4xl text-font-header hover:text-sky-700 transition-colors z-[100001] font-bold"
              aria-label="Chiudi menu"
              onClick={closeMenu}
            >
              &times;
            </button>

            {/* Menu Content */}
            <nav className="flex flex-col items-start pt-8 pb-8 px-4 sm:px-6 h-full overflow-y-auto">
              {menuContent?.map((menuItem) => {
                switch (menuItem.Type) {
                  case "Route": {
                    if (menuItem.Route?.IsMainRoute) {
                      return (
                        <ButtonFullWidth
                          key={"hamburger-" + menuItem.Route?.Name}
                          onClick={() => navigateWithTransition(menuItem.Route?.Route ?? "")}
                        >
                          {t("Menu." + menuItem.Route?.Name)}
                        </ButtonFullWidth>
                      );
                    }
                    // Per ora ignoriamo i submenu nell'hamburger per semplicità
                    return null;
                  }
                  case "Language": {
                    return (
                      <LanguageSwitch
                        key="hamburger-language"
                        setIsAllMenuOpen={setIsAllMenuOpen}
                        isAllMenuOpen={isAllMenuOpen}
                        languages={menuItem.Languages}
                        isInline={false}
                      />
                    );
                  }
                  case "Link": {
                    return <div key={menuItem.Name}></div>;
                  }
                  default: {
                    return <div key={menuItem.Name}></div>;
                  }
                }
              })}
            </nav>
          </div>,
          document.body
        )
      }
    </>
  );
}

export default HamburgerMenu;
